import {
  FetchCacheItemSources,
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemGqlQuery,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchSmartContractsCacheKey,
  FetchCacheSmartContractsFilters,
  FetchSmartContractsGqlQuery,
  FetchSmartContractsLimit,
  FetchSmartContractsPage,
  FetchSmartContractsSort
} from '../../smartContractsTypes';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_SMART_CONTRACTS_FILTERS,
  INITIAL_SMART_CONTRACTS_LIMIT,
  INITIAL_SMART_CONTRACTS_PAGE,
  INITIAL_SMART_CONTRACTS_SORT,
  SmartContractsPermissions
} from '../../smartContractsConstants';

interface SmartContractsDefaultOptions {
  cacheKey: FetchSmartContractsCacheKey;
  initialFilters?: FetchCacheSmartContractsFilters;
  initialLimit?: FetchSmartContractsLimit;
  initialPage?: FetchSmartContractsPage;
  initialSort?: FetchSmartContractsSort;
  query: FetchSmartContractsGqlQuery;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    withoutPrefetch?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    keepPreviousData?: boolean;
    staleTime?: number;
  };
  action?: SmartContractsPermissions;
}

interface SmartContractsWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery?: FetchItemsGqlQuery;
  fetchCacheItemSource?: FetchCacheItemSources;
  fetchCacheItemQuery?: FetchItemGqlQuery;
}

interface SmartContractsWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
  fetchCacheItemSource?: never;
  fetchCacheItemQuery?: never;
}

type SmartContractsOptions = SmartContractsDefaultOptions &
  (
    | SmartContractsWithPrefetchItemOptions
    | SmartContractsWithoutPrefetchItemOptions
  );

const scope = 'smartContracts';

function useCachePaginatedSmartContracts<ItemType>({
  action = SmartContractsPermissions.READ_SMART_CONTRACTS_INDEX_ES_QUERY,
  cacheKey,
  fetchItemCacheKey,
  fetchItemQuery,
  fetchCacheItemQuery,
  fetchCacheItemSource = FetchCacheItemSources.DB,
  initialFilters = INITIAL_SMART_CONTRACTS_FILTERS,
  initialLimit = INITIAL_SMART_CONTRACTS_LIMIT,
  initialPage = INITIAL_SMART_CONTRACTS_PAGE,
  initialSort = INITIAL_SMART_CONTRACTS_SORT,
  options = {},
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits = false
}: SmartContractsOptions) {
  const {
    changeItemsFilters,
    clearItemsFilters,
    currentFilters,
    currentLimit,
    currentPage,
    currentSort,
    data,
    filterItems,
    isFetched,
    isLoading,
    isPlaceholderData,
    items,
    itemsError,
    itemsTotalCount,
    limitItems,
    paginateItems,
    prefetchItem,
    prefetchItems,
    sortItems,
    updateItemCache,
    updateItemsCache
  } = useCacheIndexQuery<ItemType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    fetchCacheItemSource,
    fetchItemCacheKey,
    fetchItemQuery,
    fetchCacheItemQuery,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    options,
    query,
    scope,
    source,
    trackTotalHits
  });

  return {
    smartContractsData: data,
    smartContracts: items,
    smartContractsError: itemsError,
    smartContractsTotalCount: itemsTotalCount,
    smartContractsFetched: isFetched,
    smartContractsLoading: isLoading,
    smartContractsIsPlaceholderData: isPlaceholderData,
    smartContractsFilters: currentFilters,
    smartContractsSort: currentSort,
    smartContractsPage: currentPage,
    smartContractsLimit: currentLimit,
    filterSmartContracts: filterItems,
    changeSmartContractsFilters: changeItemsFilters,
    clearSmartContractsFilters: clearItemsFilters,
    sortSmartContracts: sortItems,
    paginateSmartContracts: paginateItems,
    limitSmartContracts: limitItems,
    prefetchSmartContracts: prefetchItems,
    prefetchSmartContract: prefetchItem,
    updateSmartContractCache: updateItemCache,
    updateSmartContractsCache: updateItemsCache
  };
}

export default useCachePaginatedSmartContracts;
